import axios from 'axios'
import { ElMessage } from 'element-plus'
import router from '@/router'

// 全局 axios 实例：baseURL 取 VITE_API_BASE（开发期经 Vite 代理转发到 :8080）
const request = axios.create({
  baseURL: import.meta.env.VITE_API_BASE || '/api',
  timeout: 15000
})

// 请求拦截器：注入 JWT
request.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('ts_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => Promise.reject(error)
)

// 上传类请求放宽超时（大文件慢网络下 15s 会先超时，表现为"网络异常"）
const UPLOAD_TIMEOUT = 300000

/**
 * 按 HTTP 状态码给出可读提示。
 * 关键点：nginx 返回 413/502 等错误时响应体是 HTML，data.message 取不到，
 * 若直接 fallback 成"网络异常"用户无法判断原因，故此处按状态码兜底文案。
 */
function messageByStatus(status, fallback) {
  switch (status) {
    case 400:
      return fallback || '请求参数有误，请检查后重试'
    case 401:
      return '登录已失效，请重新登录'
    case 403:
      return fallback || '没有权限执行该操作'
    case 404:
      return fallback || '请求的资源不存在'
    case 408:
      return '请求超时，请检查网络后重试'
    case 413:
      return '文件过大，服务器拒绝接收。请压缩文件或减少单次上传数量后重试'
    case 429:
      return fallback || '操作过于频繁，请稍后重试'
    case 500:
      return fallback || '服务器内部错误，请稍后重试'
    case 502:
    case 503:
    case 504:
      return '服务暂时不可用，请稍后重试（若正在上传大文件，请稍后重试或压缩后上传）'
    default:
      return fallback || '网络异常，请稍后重试'
  }
}

// 响应拦截器：统一解包、业务错误提示、401 跳登录
request.interceptors.response.use(
  async (response) => {
    const body = response.data
    // 文件下载（responseType: 'blob'）：后端业务异常仍以 HTTP 200 + JSON 体返回，
    // 此时 body 是一个"内容其实是 JSON 的 Blob"。若不识别，调用方会把这段 JSON
    // 当作文件存成 .zip / .xlsx，用户打开时表现为"文件损坏、无法解压"。
    if (body instanceof Blob) {
      const looksLikeJson = body.type && body.type.includes('application/json')
      if (looksLikeJson) {
        try {
          const parsed = JSON.parse(await body.text())
          if (parsed && typeof parsed.code === 'number' && parsed.code !== 0) {
            ElMessage.error(parsed.message || '请求失败')
            return Promise.reject(new Error(parsed.message || 'business error'))
          }
        } catch {
          // 解析失败说明确实是二进制文件，按正常下载放行
        }
      }
      return body
    }
    // 统一响应结构 { code, message, data }；code=0 成功
    if (body && typeof body.code === 'number' && body.code !== 0) {
      ElMessage.error(body.message || '请求失败')
      return Promise.reject(new Error(body.message || 'business error'))
    }
    return body
  },
  async (error) => {
    const status = error.response?.status
    // blob 请求出错时 error.response.data 也是 Blob，需先解析出真实错误消息，
    // 否则提示恒为"网络异常"，掩盖了真正的越权/参数错误原因
    if (error.response?.data instanceof Blob) {
      try {
        const parsed = JSON.parse(await error.response.data.text())
        if (parsed && parsed.message) {
          error.message = parsed.message
          error.response.data = parsed
        }
      } catch {
        // 非 JSON 错误体，保持原样
      }
    }
    if (status === 401) {
      localStorage.removeItem('ts_token')
      localStorage.removeItem('ts_user')
      localStorage.removeItem('ts_current_role')
      // 同步重置 Pinia 登录态：仅清 localStorage 时 store 内 token 仍在，
      // 守卫会认为"已登录"而把 /login 又弹回首页，形成跳转循环。
      import('@/stores/auth').then((m) => m.useAuthStore().logout()).catch(() => {})
      // 软导航到登录页，避免整页刷新破坏 SPA 视图（否则切换模块会白屏、需手动刷新）
      if (router.currentRoute.value.path !== '/login') {
        router.replace('/login')
      }
      ElMessage.error('登录已失效，请重新登录')
      // 必须 reject：旧实现 resolve 出 data:null，调用方 `.then(r => r.data)` 拿到 null 后
      // 再执行 `data.records` 会抛 "Cannot read properties of null (reading 'records')"。
      // 改为显式失败，由调用方 catch/finally 收口；登录页由上面的软导航接管。
      const authErr = new Error('登录已失效，请重新登录')
      // 标记为"登录失效"：main.js 的错误兜底会跳过它，避免在跳转登录页的同时
      // 额外弹出红色运行时错误条（onMounted 里未 catch 的 await 会走 Vue errorHandler）。
      authErr.isAuthError = true
      return Promise.reject(authErr)
    }
    // axios 无 response 时（超时/断网/CORS）走 error.code
    if (!error.response) {
      if (error.code === 'ECONNABORTED' || /timeout/i.test(error.message || '')) {
        ElMessage.error('请求超时，请检查网络后重试（大文件上传请耐心等待）')
      } else {
        ElMessage.error('网络连接失败，请检查网络后重试')
      }
      return Promise.reject(error)
    }
    // 后端返回了 JSON message 时优先用它（更贴近业务语义）
    const bizMsg = error.response?.data?.message
    ElMessage.error(bizMsg || messageByStatus(status))
    return Promise.reject(error)
  }
)

export default request
export { UPLOAD_TIMEOUT }
